import Button from '@/components/Button';
import DeliveryWindowSelectBox, { DeliveryWindowItem } from '@/components/DeliveryWindowSelectBox';
import SpinnerIcon from '@/components/icons/SpinnerIcon';
import PageHeader from '@/components/PageHeader';
import { useSettings } from '@/contexts/SettingsContext';
import { useAuth } from '@/modules/auth/contexts/AuthContext';
import CartMoqAlert from '@/modules/store/components/CartMoqAlert';
import CartTable from '@/modules/store/components/CartTable';
import { useCart } from '@/modules/store/contexts/CartContext';
import { findStoreAddress, StoreAddressType } from '@/modules/store/models/Store';
import CartEmpty from '@/modules/store/pages/CartEmpty';
import { CheckoutReviewPath, ShopPath } from '@/modules/store/paths';
import useCartQuery from '@/modules/store/queries/useCartQuery';
import CartDetailsService from '@/modules/store/services/CartDetailsService';
import { Link } from 'react-router-dom';

const Cart = () => {
  const { currencyCode } = useSettings();

  const { currentUser } = useAuth();
  const { storeId } = currentUser();

  const {
    getCarts,
    cartItemCount,
    updateItemFromCart,
    removeItemFromCart,
    updateDeliveryWindow,
    updateSelectedCartItems,
    resetCart,
    canCheckout,
    addedSkuIds,
    currentSkuIds,
  } = useCart();

  const { isLoading, skus, suppliers, store, isOrderPredictionAvailable } = useCartQuery(
    addedSkuIds,
    currentSkuIds,
    storeId,
  );

  if (isLoading) {
    return (
      <div className="flex justify-center py-20">
        <SpinnerIcon />
      </div>
    );
  }

  if (!isLoading && cartItemCount == 0) {
    return <CartEmpty />;
  }

  const handleOrderQuantityChange = (quantity: number, supplierId: string, itemId: number) => {
    updateItemFromCart(supplierId, itemId, quantity);
  };

  const handleCheckedCartItemsChange = (items: number[], supplierId: string) => {
    updateSelectedCartItems(items, supplierId);
  };

  const handleDeliveryWindowChange = (
    deliveryWindow: DeliveryWindowItem | null,
    supplierId: string,
  ) => {
    updateDeliveryWindow(supplierId, deliveryWindow?.deliveryWindow || null);
  };

  const handleRemoveItemFromCart = (supplierId: string, itemId: number) => {
    removeItemFromCart(supplierId, itemId);
  };

  const handleResetCart = () => {
    resetCart();
  };

  const carts = getCarts(suppliers);

  const cartDetailsService = new CartDetailsService();

  const cartDetails = cartDetailsService.getCartDetails(carts, skus, suppliers, currencyCode);
  const checkoutDisabled = !canCheckout(cartDetails);
  const taxCountryCode = findStoreAddress(store?.addresses, [
    StoreAddressType.BILLING,
    StoreAddressType.BUSINESS,
    StoreAddressType.DELIVERY,
  ])?.countryCode;

  return (
    <>
      <PageHeader title="Cart">
        <Button data-testid="cart-clear-button" onClick={handleResetCart} variant="secondary">
          Clear Cart
        </Button>
        <Button data-testid="cart-continue-shopping-button" to={ShopPath.path} variant="secondary">
          Continue Shopping
        </Button>
        <Button
          data-testid="cart-checkout-button"
          to={CheckoutReviewPath}
          disabled={checkoutDisabled}
        >
          Checkout
        </Button>
      </PageHeader>

      {cartDetails.map((cart) => (
        <div
          key={cart.supplierId}
          className="mb-8"
          data-testid="cart-supplier-section"
          data-supplier-name={cart.supplierName}
          data-supplier-id={cart.supplierId}
        >
          <h2
            className="text-xl font-bold mb-4"
            data-testid="cart-supplier-heading"
            data-supplier-name={cart.supplierName}
            data-supplier-id={cart.supplierId}
          >
            <Link to={ShopPath.withSupplierId(cart.supplierId)}>{cart.supplierName}</Link>
          </h2>

          <CartMoqAlert cart={cart} />

          <div className="mb-4 lg:w-96">
            <DeliveryWindowSelectBox
              value={cart.deliveryWindow?.id || null}
              onSelect={(item) => handleDeliveryWindowChange(item, cart.supplierId)}
              supplierId={cart.supplierId}
              defaultValue="first"
            />
          </div>

          <CartTable
            cart={cart}
            onChangeOrderQuantity={handleOrderQuantityChange}
            onChangeCheckedItems={handleCheckedCartItemsChange}
            removeItem={handleRemoveItemFromCart}
            showVat={store?.showVat}
            isOrderPredictionAvailable={isOrderPredictionAvailable}
            countryCode={taxCountryCode}
          />
        </div>
      ))}

      <div className="flex flex-col gap-2 mt-6 mb-4 sm:flex-row sm:items-center sm:justify-end">
        <Button data-testid="cart-continue-shopping-button" to={ShopPath.path} variant="secondary">
          Continue Shopping
        </Button>

        <Button
          data-testid="cart-checkout-button"
          to={CheckoutReviewPath}
          disabled={checkoutDisabled}
        >
          Checkout
        </Button>
      </div>
    </>
  );
};

export default Cart;
